import React, { useRef } from 'react';
import { Input } from 'reactstrap';
import BootstrapTable from '../../../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { SellingOwnerEmploymentAgreementRow } from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermsFormSelect } from './TermsFormSelect';

interface TableRow {
    tableRowId: number;
    customRowId: string;
}

interface OwnerEmploymentAgreementDurationTableProps {
    columns: string[];
    tableRows: TableRow[];
    rows: SellingOwnerEmploymentAgreementRow[];
    isReadonly: boolean;
    hasError: boolean;
    onChange: (rows: SellingOwnerEmploymentAgreementRow[]) => void;
    rowDisplayData?: Record<string, Record<string, string>>;
    // Review views: an owner without a saved row predates the mandatory Yes/No answer
    // and effectively answered "No"; on the initial submission the answer stays blank
    // until the buyer explicitly picks one.
    treatMissingAsNo?: boolean;
}

interface FormatterExtra {
    rows: SellingOwnerEmploymentAgreementRow[];
    isReadonly: boolean;
    hasError: boolean;
    treatMissingAsNo: boolean;
}

const EMPLOYED_POST_CLOSE_OPTIONS = [{ value: 'Yes' }, { value: 'No' }] as const;

// A saved row without an explicit answer is legacy data — rows were only created
// when the old "Employed Post-Close" checkbox was checked, so it means "Yes".
const resolveAnswer = (
    savedRow: SellingOwnerEmploymentAgreementRow | undefined,
    treatMissingAsNo: boolean
): boolean | null => {
    if (savedRow) {
        return savedRow.isEmployedPostClose ?? true;
    }
    return treatMissingAsNo ? false : null;
};

export const OwnerEmploymentAgreementDurationTable = ({
    columns,
    tableRows,
    rows,
    isReadonly,
    hasError,
    onChange,
    rowDisplayData,
    treatMissingAsNo = false,
}: OwnerEmploymentAgreementDurationTableProps) => {
    const handleAnswerChange = (tableRowId: number, value: string | null) => {
        if (value === null) {
            onChange(rows.filter((r) => r.tableRowId !== tableRowId));
            return;
        }
        const isEmployed = value === 'Yes';
        const existing = rows.find((r) => r.tableRowId === tableRowId);
        if (existing) {
            onChange(rows.map((r) =>
                r.tableRowId === tableRowId
                    ? { ...r, isEmployedPostClose: isEmployed, durationMonths: isEmployed ? r.durationMonths : null }
                    : r
            ));
        } else {
            onChange([...rows, { id: crypto.randomUUID(), tableRowId, durationMonths: null, isEmployedPostClose: isEmployed }]);
        }
    };

    const handleDurationChange = (tableRowId: number, durationMonths: number | null) => {
        onChange(rows.map((r) => r.tableRowId === tableRowId ? { ...r, durationMonths } : r));
    };

    const handleAnswerChangeRef = useRef(handleAnswerChange);
    handleAnswerChangeRef.current = handleAnswerChange;
    const handleDurationChangeRef = useRef(handleDurationChange);
    handleDurationChangeRef.current = handleDurationChange;

    const hasAnyEmployed = rows.some((r) => (r.isEmployedPostClose ?? true) === true);

    const tableColumns = [
        ...columns.map((col) => ({
            dataField: col,
            text: col,
            isDummyField: true,
            formatter: (_: unknown, row: TableRow) => rowDisplayData?.[row.customRowId]?.[col] ?? '',
        })),
        {
            dataField: 'employmentAgreement',
            text: 'Employed Post-Close',
            isDummyField: true,
            headerStyle: { textAlign: 'center' as const },
            style: { textAlign: 'center' as const },
            formatExtraData: { rows, isReadonly, hasError, treatMissingAsNo } as FormatterExtra,
            formatter: (_: unknown, row: TableRow, _rowIndex: number, extra: FormatterExtra) => {
                const savedRow = extra.rows.find((r) => r.tableRowId === row.tableRowId);
                const answer = resolveAnswer(savedRow, extra.treatMissingAsNo);
                return (
                    <TermsFormSelect
                        value={answer === null ? '' : answer ? 'Yes' : 'No'}
                        options={EMPLOYED_POST_CLOSE_OPTIONS}
                        className="owner-employment-post-close-select"
                        disabled={extra.isReadonly}
                        invalid={extra.hasError && answer === null}
                        onChange={(value) => handleAnswerChangeRef.current(row.tableRowId, value)}
                        aria-label="Employed Post-Close"
                    />
                );
            },
        },
        ...(hasAnyEmployed ? [{
            dataField: 'durationMonths',
            text: 'Employment Term',
            isDummyField: true,
            headerStyle: { textAlign: 'center' as const },
            style: { textAlign: 'center' as const },
            formatExtraData: { rows, isReadonly, hasError, treatMissingAsNo } as FormatterExtra,
            formatter: (_: unknown, row: TableRow, _rowIndex: number, extra: FormatterExtra) => {
                const savedRow = extra.rows.find((r) => r.tableRowId === row.tableRowId);
                const answer = resolveAnswer(savedRow, extra.treatMissingAsNo);
                if (answer !== true || !savedRow) return null;
                return (
                    <Input
                        type="number"
                        bsSize="sm"
                        className="owner-employment-duration-select"
                        value={savedRow.durationMonths ?? ''}
                        min={1}
                        step={1}
                        disabled={extra.isReadonly}
                        invalid={extra.hasError && savedRow.durationMonths === null}
                        onChange={(e) =>
                            handleDurationChangeRef.current(
                                row.tableRowId,
                                e.target.value ? Math.trunc(Number(e.target.value)) : null
                            )
                        }
                        onKeyDown={(e) => {
                            if (e.key === '.' || e.key === ',') e.preventDefault();
                        }}
                        placeholder="Months"
                    />
                );
            },
        }] : []),
    ];

    return (
        <div className="loi-owner-employment-agreement-table table-responsive">
            <BootstrapTable
                bootstrap4
                keyField="tableRowId"
                data={tableRows}
                columns={tableColumns}
                noDataIndication="No owners available"
            />
        </div>
    );
};
